import { useId } from 'react';
import { Button, CloseIcon, Dialog, InputText } from '@repo/atomic-components';
import { ChatAccessType, ChatInvitedUser, ChatShareCandidate } from '@/api/chatSharing';
import { TITLE_MAX_LENGTH } from '@/ui/features/chat/constants';
import { ChatShareRecipientSelect } from './ChatShareRecipientSelect';
import { ChatShareRow } from './ChatShareRow';

export interface ChatSettingsDialogProps {
  visible: boolean;
  onHide: () => void;
  title: string;
  onTitleChange: (title: string) => void;
  users: ChatInvitedUser[];
  candidates: ChatShareCandidate[];
  /** The name to show for someone, with their email when another candidate has the same name. */
  getUserLabel: (user: { userId: string; name: string }) => string;
  /** The candidate list is unavailable — e.g. the user belongs to no department to share within. */
  hasCandidatesError: boolean;
  isLoading: boolean;
  isSaving: boolean;
  canSave: boolean;
  onAddUser: (candidate: ChatShareCandidate) => void;
  onAccessTypeChange: (user: ChatInvitedUser, accessType: ChatAccessType) => void;
  onRemoveUser: (user: ChatInvitedUser) => void;
  onSave: () => void;
}

export const ChatSettingsDialog = ({
  visible,
  onHide,
  title,
  onTitleChange,
  users,
  candidates,
  getUserLabel,
  hasCandidatesError,
  isLoading,
  isSaving,
  canSave,
  onAddUser,
  onAccessTypeChange,
  onRemoveUser,
  onSave,
}: ChatSettingsDialogProps) => {
  const titleInputId = useId();
  const searchInputId = useId();
  const excludedUserIds = new Set(users.map((user) => user.userId));

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      modal
      draggable={false}
      closable={false}
      header={
        <div className="text-theme-black flex w-full items-center justify-between gap-3">
          <span className="text-xl leading-[1.35] font-semibold">Chat settings</span>
          <Button
            type="button"
            onClick={onHide}
            outlined
            className="text-theme-text-grey hover:bg-theme-background-grey hover:text-theme-black flex h-6 w-6 items-center justify-center rounded-sm border-none p-0 transition-colors"
            aria-label="Close chat settings"
          >
            <CloseIcon className="h-[12px] w-[12px]" />
          </Button>
        </div>
      }
      className="!w-[min(32.5rem,calc(100vw-3rem))]"
      pt={{
        root: { className: 'rounded-lg shadow-[0_0.5rem_2rem_rgba(0,0,0,0.15)]' },
        header: { className: 'cursor-default !rounded-t-lg bg-theme-white text-theme-black px-8 pt-8 pb-0' },
        headerTitle: { className: '!m-0 !w-full' },
        content: { className: 'bg-theme-white rounded-b-lg px-8 pt-5 pb-8' },
      }}
    >
      <div className="flex flex-col gap-5">
        <div className="flex flex-col gap-1.5">
          <label htmlFor={titleInputId} className="!text-theme-black text-sm leading-[1.35] font-semibold">
            Chat title
          </label>
          <InputText
            id={titleInputId}
            value={title}
            maxLength={TITLE_MAX_LENGTH}
            disabled={isSaving}
            onChange={(event) => onTitleChange(event.target.value)}
            className="!border-theme-border-grey !bg-theme-white !text-theme-black w-full !rounded-sm !px-3 !py-2 !text-sm !leading-[1.35] !shadow-none"
          />
        </div>

        <div className="flex flex-col gap-2">
          <span className="text-theme-black text-sm leading-[1.35] font-semibold">Chat sharing</span>
          <p className="text-theme-text-grey text-xs leading-5">
            Viewers can only see the chat. Collaborators can see the chat and also send their own prompts. Chat link can
            then be shared with anyone you&apos;ve granted visibility.
          </p>
          <ChatShareRecipientSelect
            inputId={searchInputId}
            candidates={candidates}
            getLabel={getUserLabel}
            excludedUserIds={excludedUserIds}
            emptyMessage={hasCandidatesError ? 'No one available to share with' : 'No matches'}
            onAdd={onAddUser}
            disabled={isLoading || isSaving}
            isLoading={isLoading}
          />
        </div>

        {/* The pending state lives inside the search field: a placeholder here would size the dialog
            to the spinner and then shrink it to the loaded list. */}
        {!isLoading && users.length > 0 && (
          // p-1 keeps the rows' focus outline inside the scroll box; -m-1 undoes the shift it causes.
          <ul className="-m-1 flex max-h-[11.25rem] flex-col gap-2 overflow-y-auto p-1" aria-label="People with access">
            {users.map((user) => (
              <ChatShareRow
                key={user.userId}
                user={user}
                label={getUserLabel(user)}
                disabled={isSaving}
                onAccessTypeChange={(accessType) => onAccessTypeChange(user, accessType)}
                onRemove={() => onRemoveUser(user)}
              />
            ))}
          </ul>
        )}

        <div className="flex justify-end gap-3">
          <Button
            type="button"
            outlined
            onClick={onHide}
            disabled={isSaving}
            className="border-theme-border-grey hover:bg-theme-background-grey bg-theme-white text-theme-black flex items-center justify-center rounded-sm px-4 py-1.5 text-sm leading-[1.35]"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={onSave}
            disabled={!canSave || isSaving}
            className="bg-theme-dark-green text-theme-white border-theme-dark-green flex items-center justify-center rounded-sm border px-4 py-1.5 text-sm leading-[1.35] font-semibold disabled:opacity-60"
          >
            Save changes
          </Button>
        </div>
      </div>
    </Dialog>
  );
};
